import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { PersonnelCoastGuardPermit } from '../../types';
import PersonnelPermitForm from '../../components/HR/PersonnelPermitForm';
import { PageHeader, FilterBar, ErrorState, StatsCards } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { usePersonnelPermits, useCreatePersonnelPermit, useUpdatePersonnelPermit, useDeletePersonnelPermit } from '../../hooks/usePersonnelPermits';
import { useToast } from '../../contexts/ToastContext';

const PersonnelCoastGuardPermits: React.FC = () => {
  const { showToast } = useToast();
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 }
  }), []);

  const { data: permits = [], isLoading, error, refetch } = usePersonnelPermits(queryOptions);

  const createMutation = useCreatePersonnelPermit(() => {
    showToast('Personnel permit created successfully', 'success');
    refetch();
    setIsFormOpen(false);
    setSelectedPermit(undefined);
  });

  const updateMutation = useUpdatePersonnelPermit(() => {
    showToast('Personnel permit updated successfully', 'success');
    refetch();
    setIsFormOpen(false);
    setSelectedPermit(undefined);
  });

  const deleteMutation = useDeletePersonnelPermit(() => {
    showToast('Personnel permit deleted successfully', 'success');
    refetch();
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState<PersonnelCoastGuardPermit | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [permitTypeFilter, setPermitTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [expiryFilter, setExpiryFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const uniqueBranches = Array.from(new Set(permits.map(p => p.branchName).filter(Boolean)));
  const uniqueLocations = Array.from(new Set(permits.map(p => p.locationAddress).filter(Boolean)));

  const filteredPermits = useMemo(() => {
    return permits.filter(permit => {
      const matchesSearch = !searchTerm || (
        permit.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permit.branchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permit.locationAddress.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permit.permitType.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesPermitType = permitTypeFilter === 'all' || permit.permitType === permitTypeFilter;
      const matchesStatus = statusFilter === 'all' || permit.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || permit.branchName === branchFilter;
      const matchesLocation = locationFilter === 'all' || permit.locationAddress === locationFilter;
      const expiryDate = new Date(permit.expiryDate);
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      const matchesExpiry = expiryFilter === 'all' ||
        (expiryFilter === 'expired' && expiryDate < today) ||
        (expiryFilter === 'expiring_soon' && expiryDate <= thirtyDaysFromNow && expiryDate >= today) ||
        (expiryFilter === 'valid' && expiryDate > thirtyDaysFromNow);
      const issueDate = new Date(permit.issueDate);
      const matchesDateFrom = !dateFromFilter || issueDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || issueDate <= new Date(dateToFilter);
      return matchesSearch && matchesPermitType && matchesStatus && matchesBranch &&
        matchesLocation && matchesExpiry && matchesDateFrom && matchesDateTo;
    });
  }, [permits, searchTerm, permitTypeFilter, statusFilter, branchFilter, locationFilter, expiryFilter, dateFromFilter, dateToFilter]);

  const stats = useMemo(() => {
    const total = filteredPermits.length;
    const active = filteredPermits.filter(p => p.status === 'active').length;
    const expired = filteredPermits.filter(p => p.status === 'expired').length;
    const expiringSoon = filteredPermits.filter(p => {
      const expiryDate = new Date(p.expiryDate);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      return expiryDate <= thirtyDaysFromNow && p.status === 'active';
    }).length;
    return { total, active, expired, expiringSoon };
  }, [filteredPermits]);

  const filters: FilterConfig[] = useMemo(() => [
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [{ value: 'all', label: 'All Vessels/HQ' }, ...uniqueBranches.map(b => ({ value: b, label: b }))] },
    { key: 'location', label: 'Location', type: 'select', value: locationFilter, onChange: setLocationFilter, options: [{ value: 'all', label: 'All Locations' }, ...uniqueLocations.map(l => ({ value: l, label: l }))] },
    { key: 'expiry', label: 'Expiry Status', type: 'select', value: expiryFilter, onChange: setExpiryFilter, options: [{ value: 'all', label: 'All' }, { value: 'valid', label: 'Valid (30+ days)' }, { value: 'expiring_soon', label: 'Expiring Soon (30 days)' }, { value: 'expired', label: 'Expired' }] },
    { key: 'dateFrom', label: 'Issue Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Issue Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ], [branchFilter, locationFilter, expiryFilter, dateFromFilter, dateToFilter, uniqueBranches, uniqueLocations]);

  const handleExport = () => {
    const exportData = filteredPermits.map(permit => ({
      'Employee Name': permit.employeeName, 'Vessel/Headquarters': permit.branchName, 'Location': permit.locationAddress,
      'Issue Date': permit.issueDate, 'Expiry Date': permit.expiryDate, 'Expiry Status': permit.status.charAt(0).toUpperCase() + permit.status.slice(1),
      'Notes': permit.notes || '', 'Created Date': new Date(permit.createdAt).toLocaleDateString(), 'Updated Date': new Date(permit.updatedAt).toLocaleDateString()
    }));
    exportToExcel(exportData, `personnel_permits_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredPermits.map(permit => ({
      'Employee Name': permit.employeeName, 'Vessel/Headquarters': permit.branchName, 'Location': permit.locationAddress,
      'Issue Date': permit.issueDate, 'Expiry Date': permit.expiryDate, 'Expiry Status': permit.status.charAt(0).toUpperCase() + permit.status.slice(1),
      'Notes': permit.notes || '', 'Created Date': new Date(permit.createdAt).toLocaleDateString(), 'Updated Date': new Date(permit.updatedAt).toLocaleDateString()
    }));
    printAsExcel(exportData, 'Personnel Coast Guard Permits');
  };

  const handleEdit = (permit: PersonnelCoastGuardPermit) => { setSelectedPermit(permit); setIsFormOpen(true); };
  const handleDelete = async (permit: PersonnelCoastGuardPermit) => {
    if (window.confirm(`Are you sure you want to delete the ${permit.permitType} permit for ${permit.employeeName}?`)) {
      try { await deleteMutation.execute(permit); } catch (e) { console.error('Delete personnel permit error:', e); }
    }
  };
  const handleAdd = () => { setSelectedPermit(undefined); setIsFormOpen(true); };
  const handleSubmit = async (data: Partial<PersonnelCoastGuardPermit>) => {
    if (selectedPermit) {
      try { await updateMutation.execute({ id: parseInt(selectedPermit.id), data: { ...selectedPermit, ...data } }); } catch (e) { console.error('Update personnel permit error:', e); }
    } else {
      try { await createMutation.execute(data); } catch (e) { console.error('Create personnel permit error:', e); }
    }
  };
  const handleCancel = () => { setIsFormOpen(false); setSelectedPermit(undefined); };
  const onClearFilters = () => { setSearchTerm(''); setPermitTypeFilter('all'); setStatusFilter('all'); setBranchFilter('all'); setLocationFilter('all'); setExpiryFilter('all'); setDateFromFilter(''); setDateToFilter(''); };

  const columns: TableColumn[] = [
    { key: 'employeeName', label: 'Employee', sortable: true },
    { key: 'issueDate', label: 'Issue Date', sortable: true, render: (v: string) => new Date(v).toLocaleDateString() },
    { key: 'expiryDate', label: 'Expiry Date', sortable: true, render: (v: string) => new Date(v).toLocaleDateString() },
    { key: 'branchName', label: 'Vessel/Headquarters', sortable: true },
    { key: 'locationAddress', label: 'Location', sortable: true },
    { key: 'status', label: 'Expiry Status', sortable: true, render: (v: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${v === 'active' ? 'bg-green-100 text-green-800' : v === 'expired' ? 'bg-red-100 text-red-800' : v === 'suspended' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{v.charAt(0).toUpperCase() + v.slice(1)}</span>
    ) }
  ];

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4" /><p className="text-gray-600">Loading personnel permits...</p></div></div>;
  if (error) return <ErrorState message="Error loading personnel permits" onRetry={refetch} entityName="personnel permits" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Personnel Coast Guard Permits" description="Manage personnel maritime permits and certifications" />
      <StatsCards stats={[{ label: 'Total Permits', value: stats.total, color: 'blue' }, { label: 'Active Permits', value: stats.active, color: 'green' }, { label: 'Expiring Soon', value: stats.expiringSoon, color: 'yellow' }, { label: 'Expired', value: stats.expired, color: 'red' }]} />
      <FilterBar searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search permits..." filters={filters} onClearFilters={onClearFilters} totalCount={permits.length} filteredCount={filteredPermits.length} entityName="permits" onAdd={handleAdd} addLabel="Add Permit" onExport={handleExport} onPrint={handlePrint} />
      <div className="card"><Table columns={columns} data={filteredPermits} onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false} /></div>
      {isFormOpen && <PersonnelPermitForm permit={selectedPermit} onSubmit={handleSubmit} onCancel={handleCancel} />}
    </div>
  );
};

export default PersonnelCoastGuardPermits;
